/**
 * The compass, behind one interface.
 *
 * Two platforms, two conventions, one normalised output: **degrees clockwise from true north,
 * in [0, 360), for the direction the top of the screen is pointing.**
 *
 * iOS Safari
 *   `DeviceOrientationEvent.requestPermission()` must be called from a user gesture, and the
 *   heading arrives as `webkitCompassHeading` — already true north, already clockwise.
 *
 * Android Chrome
 *   No permission call, but the absolute heading only arrives on `deviceorientationabsolute`,
 *   as `alpha`, which is *counter*clockwise: heading = 360 - alpha.
 *
 * Both then need the same correction. `screen.orientation.angle` is how far the content has
 * been rotated clockwise from the device's natural orientation, so screen-up is the device's
 * top rotated clockwise by that angle:
 *
 *     heading(screen-up) = heading(device-top) + screen.orientation.angle
 *
 * Sanity check: facing north, phone upright, top points north — angle 0, heading 0. Turn the
 * phone 90° anticlockwise in your hands while still facing north: the top now points west
 * (270°) and `angle` reads 90, so the screen-up heading is 270 + 90 = 0. Still north. Correct.
 */

import { normalizeDegrees } from '@beeline/shared';

export type HeadingPermission = 'granted' | 'denied' | 'unsupported' | 'unknown';

export interface HeadingReading {
  /** Degrees clockwise from true north, for the direction the top of the screen points. */
  heading: number;
  /** False when the sensor is only reporting relative rotation, which is not a compass. */
  absolute: boolean;
  /** iOS reports a confidence cone in degrees; -1 means "unreliable". Null elsewhere. */
  accuracyDegrees: number | null;
  source: 'ios' | 'absolute' | 'relative';
  at: number;
}

/** If nothing arrives in this long after starting, the sensor is not going to work. */
const SILENCE_TIMEOUT_MS = 3000;

interface WebkitDeviceOrientationEvent extends DeviceOrientationEvent {
  webkitCompassHeading?: number;
  webkitCompassAccuracy?: number;
}

type PermissionRequester = { requestPermission?: () => Promise<PermissionState | string> };

/** iOS gates device orientation behind an explicit, gesture-driven permission call. */
export const headingNeedsPermission = (): boolean =>
  typeof window !== 'undefined' &&
  typeof (DeviceOrientationEvent as unknown as PermissionRequester)?.requestPermission ===
    'function';

export const headingIsSupported = (): boolean =>
  typeof window !== 'undefined' && 'DeviceOrientationEvent' in window;

/**
 * How far the page content is rotated from the device's natural orientation.
 * `window.orientation` is the fallback for Safari versions without `screen.orientation`.
 */
function screenAngle(): number {
  const angle = window.screen?.orientation?.angle;
  if (typeof angle === 'number') return angle;

  const legacy = (window as unknown as { orientation?: number }).orientation;
  return typeof legacy === 'number' ? legacy : 0;
}

export class HeadingProvider {
  private listener: ((event: DeviceOrientationEvent) => void) | null = null;
  private eventName: 'deviceorientationabsolute' | 'deviceorientation' | null = null;
  private silenceTimer: number | null = null;
  private onReading: ((reading: HeadingReading) => void) | null = null;
  private onUnavailable: (() => void) | null = null;
  private lastReading: HeadingReading | null = null;

  get latest(): HeadingReading | null {
    return this.lastReading;
  }

  /**
   * Ask for permission. Must be called straight from a tap: iOS rejects it otherwise, and the
   * rejection looks identical to a denial.
   */
  async requestPermission(): Promise<HeadingPermission> {
    if (!headingIsSupported()) return 'unsupported';

    const requester = DeviceOrientationEvent as unknown as {
      requestPermission?: (absolute?: boolean) => Promise<PermissionState | string>;
    };
    if (typeof requester.requestPermission !== 'function') return 'unknown';

    try {
      // Ask for absolute orientation where that is a separate grant; older iOS ignores the
      // argument, which is exactly the behaviour we want.
      const state = await requester.requestPermission(true);
      return state === 'granted' ? 'granted' : 'denied';
    } catch {
      // Thrown when the call did not come from a user gesture, or the user dismissed it.
      return 'denied';
    }
  }

  /**
   * Start listening.
   *
   * @param onReading called on every sensor event
   * @param onUnavailable called once if nothing arrives — the caller should fall back to the
   *        north-up view rather than show an arrow that never moves
   */
  start(onReading: (reading: HeadingReading) => void, onUnavailable?: () => void): void {
    if (this.listener) return;
    if (!headingIsSupported()) {
      onUnavailable?.();
      return;
    }

    this.onReading = onReading;
    this.onUnavailable = onUnavailable ?? null;

    // `deviceorientationabsolute` is the only event that carries a true-north heading on
    // Android. iOS does not have it, and puts the compass on plain `deviceorientation`.
    this.eventName =
      'ondeviceorientationabsolute' in window ? 'deviceorientationabsolute' : 'deviceorientation';

    this.listener = (event: DeviceOrientationEvent) => this.handle(event);
    window.addEventListener(this.eventName, this.listener, true);

    this.silenceTimer = window.setTimeout(() => {
      if (this.lastReading) return;
      this.onUnavailable?.();
    }, SILENCE_TIMEOUT_MS);
  }

  stop(): void {
    if (this.listener && this.eventName) {
      window.removeEventListener(this.eventName, this.listener, true);
    }
    if (this.silenceTimer !== null) window.clearTimeout(this.silenceTimer);

    this.listener = null;
    this.eventName = null;
    this.silenceTimer = null;
    this.onReading = null;
    this.onUnavailable = null;
  }

  private handle(event: DeviceOrientationEvent): void {
    const reading = readingFrom(event, screenAngle());
    if (!reading) return;

    if (this.silenceTimer !== null) {
      window.clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }

    this.lastReading = reading;
    this.onReading?.(reading);
  }
}

/**
 * Turn one sensor event into a normalised reading, or null if it carries no heading.
 * Pure, and exported, because this conversion is the single most breakable thing in the app.
 */
export function readingFrom(
  event: DeviceOrientationEvent,
  angle: number,
  at: number = Date.now(),
): HeadingReading | null {
  const webkit = event as WebkitDeviceOrientationEvent;

  // iOS: already true north, already clockwise.
  if (typeof webkit.webkitCompassHeading === 'number') {
    const accuracy = webkit.webkitCompassAccuracy;
    return {
      heading: normalizeDegrees(webkit.webkitCompassHeading + angle),
      // A negative accuracy is iOS's way of saying the magnetometer needs calibrating.
      absolute: typeof accuracy !== 'number' || accuracy >= 0,
      accuracyDegrees: typeof accuracy === 'number' ? accuracy : null,
      source: 'ios',
      at,
    };
  }

  if (typeof event.alpha !== 'number' || !Number.isFinite(event.alpha)) return null;

  // Everywhere else: alpha counts anticlockwise, so it has to be flipped.
  return {
    heading: normalizeDegrees(360 - event.alpha + angle),
    absolute: event.absolute === true,
    accuracyDegrees: null,
    source: event.absolute === true ? 'absolute' : 'relative',
    at,
  };
}
